import React, { useRef } from 'react';
import styled, { useTheme } from 'styled-components';

import { useComponentSize } from 'Util/Hooks';

import { Box, Flex, SvgIcon } from '../../Base';
import { IS_SHOW_VIDEO_RESOLUTION } from '../../../Util/Capacitors/constant';
import VjsComponent from '../VjsComponent';
import withConfig from '../../../Context/ConfigContext/hoc';

const MobileVideoControllerWrapper = styled(Flex)`
  z-index: 2;
  flex: 1;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const VideoPlayerControlWrapper = styled(Flex)`
  z-index: 1;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  transform: ${({ isPAAS }) => (isPAAS ? `translateY(20px)` : `translateY(0)`)};

  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    transform: translateY(0);
  }
`;

const VideoController = ({
  player,
  disableFullscreen,
  showVideoResolution,
  appConfig: { uiVariant },
}) => {
  const videoWrapperRef = useRef();
  const { _breakpoints } = useTheme();
  const videoSize = useComponentSize(videoWrapperRef);
  const isSmallVideo = videoSize.width < _breakpoints[0];
  const isMediumVideo = videoSize.width < _breakpoints[1];

  const isShowVideoResolution = IS_SHOW_VIDEO_RESOLUTION && showVideoResolution;
  return (
    <Box ref={videoWrapperRef}>
      <Box display={isMediumVideo ? 'block' : 'none'}>
        <MobileVideoControllerWrapper>
          <VjsComponent
            player={player}
            componentType={VjsComponent.COMPONENT_TYPES.SEEK_BUTTON}
            options={{ direction: 'back', seconds: 10 }}
            display={isSmallVideo ? 'none' : 'block'}
            ml={4}
          />
          <VjsComponent player={player} componentType={VjsComponent.COMPONENT_TYPES.PLAY_TOGGLE} />
          <VjsComponent
            player={player}
            componentType={VjsComponent.COMPONENT_TYPES.SEEK_BUTTON}
            options={{ direction: 'forward', seconds: 10 }}
            display={isSmallVideo ? 'none' : 'block'}
            mr={4}
          />
        </MobileVideoControllerWrapper>
        <VideoPlayerControlWrapper isPAAS={uiVariant === 'PAAS'}>
          <Flex>
            <VjsComponent
              player={player}
              componentType={VjsComponent.COMPONENT_TYPES.PLAYBACK_RATE_MENU_BUTTON}
              options={{ playbackRates: [0.75, 1.0, 1.25, 1.5, 1.75, 2.0] }}
              display={isSmallVideo ? 'none' : 'block'}
            />
            <VjsComponent
              player={player}
              componentType={VjsComponent.COMPONENT_TYPES.VOLUME_PANEL}
              options={{ inline: false }}
            />
          </Flex>
          <Flex>
            {isShowVideoResolution && (
              <VjsComponent
                player={player}
                componentType={VjsComponent.COMPONENT_TYPES.QUALITY_SELECTOR_BUTTON}
              >
                <Flex
                  justifyContent={'center'}
                  alignItems={'center'}
                  pb={3}
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '46px',
                    height: '46px',
                  }}
                >
                  <SvgIcon icon={SvgIcon.ICON_LIST.SETTINGS} color={'white'} size={24} />
                </Flex>
              </VjsComponent>
            )}
            {!disableFullscreen && (
              <VjsComponent
                player={player}
                componentType={VjsComponent.COMPONENT_TYPES.FULL_SCREEN_TOGGLE}
                options={{ navigationUI: 'auto' }}
              />
            )}
          </Flex>
        </VideoPlayerControlWrapper>
      </Box>
      <Flex width={'100%'} alignItems="center" justifyContent={'center'}>
        <VjsComponent
          player={player}
          componentType={VjsComponent.COMPONENT_TYPES.CURERNT_TIME_DISPLAY}
        />
        <VjsComponent
          player={player}
          componentType={VjsComponent.COMPONENT_TYPES.PROGRESS_CONTROL}
          width={'100%'}
          flex={1}
        />
        <VjsComponent
          player={player}
          componentType={VjsComponent.COMPONENT_TYPES.DURATION_DISPLAY}
        />
      </Flex>
      <Box display={isMediumVideo ? 'none' : 'block'}>
        <Flex width={'100%'} justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" flex="1 0" />
          <Flex alignItems="center" justifyContent="center" flex="2 0">
            <VjsComponent
              player={player}
              componentType={VjsComponent.COMPONENT_TYPES.SEEK_BUTTON}
              options={{ direction: 'back', seconds: 10 }}
              ml={'auto'}
            />
            <VjsComponent
              player={player}
              componentType={VjsComponent.COMPONENT_TYPES.PLAY_TOGGLE}
            />
            <VjsComponent
              player={player}
              componentType={VjsComponent.COMPONENT_TYPES.SEEK_BUTTON}
              options={{ direction: 'forward', seconds: 10 }}
              mr={'auto'}
            />
          </Flex>
          <Flex alignItems="center" justifyContent="flex-end" flex="1 0">
            {isShowVideoResolution && (
              <VjsComponent
                player={player}
                componentType={VjsComponent.COMPONENT_TYPES.QUALITY_SELECTOR_BUTTON}
              >
                <Flex
                  justifyContent={'center'}
                  alignItems={'center'}
                  pb={3}
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '46px',
                    height: '46px',
                  }}
                >
                  <SvgIcon icon={SvgIcon.ICON_LIST.SETTINGS} color={'white'} size={24} />
                </Flex>
              </VjsComponent>
            )}
            <VjsComponent
              player={player}
              componentType={VjsComponent.COMPONENT_TYPES.PLAYBACK_RATE_MENU_BUTTON}
              options={{ playbackRates: [0.75, 1.0, 1.25, 1.5, 1.75, 2.0] }}
            />
            <Box display={isShowVideoResolution ? ['none', 'block'] : 'block'}>
              <VjsComponent
                player={player}
                componentType={VjsComponent.COMPONENT_TYPES.VOLUME_PANEL}
                options={{ inline: false }}
              />
            </Box>
            {!disableFullscreen && (
              <VjsComponent
                player={player}
                componentType={VjsComponent.COMPONENT_TYPES.FULL_SCREEN_TOGGLE}
                options={{ navigationUI: 'auto' }}
              />
            )}
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default withConfig(VideoController);
